import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { subscribeNotificationInsert, subscribeNotificationReconnect } from "@/lib/notificationsBus";
import { useRealtimeSelfHeal } from "@/hooks/useRealtimeSelfHeal";

/**
 * Data-refresh subscriber for broker/shipper marketplace offers.
 * Toast + chime are owned by useGlobalNotificationAlerts — this hook only
 * invalidates React Query caches so open UIs update in realtime.
 */
export function useBrokerOfferNotifications() {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const subscribedRef = useRef(false);

  const isBrokerLike =
    profile?.user_type === "broker" ||
    profile?.user_type === "broker_carrier" ||
    profile?.user_type === "shipper";

  useEffect(() => {
    if (!user || !isBrokerLike || subscribedRef.current) return;
    subscribedRef.current = true;

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ["broker-offers", user.id] });
    };

    const offersChannel = supabase
      .channel("broker-offer-notifications")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "offers" },
        async (payload) => {
          const newOffer = payload.new as {
            id: string;
            load_id: string;
            created_by: string;
          };
          if (newOffer.created_by === user.id) return;
          const { data: load } = await supabase
            .from("loads")
            .select("created_by")
            .eq("id", newOffer.load_id)
            .single();
          if (!load || load.created_by !== user.id) return;
          invalidate();
        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") invalidate();
      });

    const relevantTypes = new Set([
      "new_offer",
      "carrier_accepted",
      "counter_offer",
      "offer_accepted_by_carrier",
      "offer_rejected_by_carrier",
    ]);
    const offNotif = subscribeNotificationInsert(user.id, (row) => {
      if (!relevantTypes.has((row as { type?: string })?.type ?? "")) return;
      invalidate();
    });
    const offReconnect = subscribeNotificationReconnect(user.id, () => invalidate());

    return () => {
      subscribedRef.current = false;
      supabase.removeChannel(offersChannel);
      offNotif();
      offReconnect();
    };
  }, [user, isBrokerLike, queryClient]);

  const userIdForHeal = user?.id;
  useRealtimeSelfHeal(
    () => { if (userIdForHeal) queryClient.invalidateQueries({ queryKey: ["broker-offers", userIdForHeal] }); },
    !!user && isBrokerLike,
  );
}
